import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import PublicFormPage from './PublicFormPage'
import { initSentry, Sentry } from './lib/sentry'
import { matchPublicRoute } from './lib/public-route'
import ApercuBandeau from './components/ApercuBandeau'

// PR #189 C24 — PaiementPage chargée via React.lazy (mitigation R-2 plan :
// SDK Stripe Embedded ~100kB chargé UNIQUEMENT sur route /paiement/{token},
// pas dans bundle global App).
const PaiementPage = React.lazy(() => import('./PaiementPage'))

// Phase A.4 (ADR-114) H-2 pessimist : lazy-load PublicLivretPage + PublicLivretQRPage.
// Justification : ces 2 pages voyageur sont 1300+ LOC + fonts Playfair/Raleway,
// pas nécessaires dans le bundle App admin (Logements + Compta + etc.).
// Avant : import direct → +35-40 kB minified dans main.js admin.
// Après : code-split en chunks séparés, chargés à la demande sur routes
//   /livret/r/:token et /livret/qr/:slug.
// Bénéfice : bundle admin allégé. Résout NQA M-PR216-1.
const PublicLivretPage = React.lazy(() => import('./PublicLivretPage'))
const PublicLivretQRPage = React.lazy(() => import('./PublicLivretQRPage'))

// Sprint A.9.3 PR-A93-1c (ADR-144) — lazy-load PublicLivretModuleDetail.
// Route /livret/r/:token/module/:moduleId → page module detail standalone.
// Chargé uniquement quand voyageur clique sur card LivretModulesGrid.
const PublicLivretModuleDetail = React.lazy(() =>
  import('./PublicLivretModuleDetail'),
)

// Sprint A.9.5b-finition (ADR-155) — PublicAlentoursCategoryPage SUPPRIMÉ.
// Pages dédiées /alentours/:category retirées (décision Dimitri post-PR #289).
// Tout passe désormais par les 7 accordéons premium expandables
// dans LivretAlentoursAutoSection (top 5 lieux par accordéon).

// Phase A.7.2b PR2 voyageur (ADR-122) — page retour Stripe Checkout Session.
// Route /services-payment/done?booking_id=<uuid> appelée par Stripe en return_url.
const ServicesPaymentReturnPage = React.lazy(() =>
  import('./ServicesPaymentReturnPage'),
)

// Chantier C3 — JourneyPage (page parcours voyageur /sejour/:token).
// Lazy-load comme PaiementPage : page voyageur standalone, hors bundle App admin.
const JourneyPage = React.lazy(() => import('./JourneyPage'))

// Chantier OTA Lot O1 — JourneyOtaPage (espace voyageur OTA /sejour-ota/:token).
// Lazy-load comme JourneyPage : page voyageur standalone, hors bundle App admin.
const JourneyOtaPage = React.lazy(() => import('./JourneyOtaPage'))

// Option B M1 (PR #962) — lien court SMS /s/:code → résolution s_resolve puis
// redirection vers /sejour/<token>. Lazy-load (page voyageur standalone).
const ShortJourneyRedirectPage = React.lazy(() =>
  import('./ShortJourneyRedirectPage'),
)

// CHANTIER LIEN COURT — lien court unifié /l/:code → résolution gl_resolve puis
// redirection vers la route interne longue du kind. Lazy-load (page voyageur
// standalone).
const GuestLinkRedirectPage = React.lazy(() => import('./GuestLinkRedirectPage'))

// WAGON D LOT D-A (ADR-194) — RecuPage (document figé /recu/:token).
// Lazy-load comme les autres pages voyageur standalone (hors bundle App admin).
const RecuPage = React.lazy(() => import('./RecuPage'))

// CHANTIER OTA Lot O3 — UnsubscribePage (désinscription marketing 1 clic,
// /unsubscribe?token=). Lazy-load (page voyageur standalone).
const UnsubscribePage = React.lazy(() => import('./UnsubscribePage'))

initSentry()

// Phase 2.E.12 — routing public minimal par pathname (pas de React Router).
// La logique de match vit désormais dans src/lib/public-route.js (PURE +
// unit-testable, LOT 3 ADR-188). Routes publiques connues :
//   /forms/<token> ET /bienvenue/<token> → PublicFormPage (alias strict LOT 3)
//   /livret/r/:token            → PublicLivretPage (UUID v4)
//   /livret/r/:token/module/:id → PublicLivretModuleDetail
//   /livret/qr/:slug            → PublicLivretQRPage (slug 2-80 [a-z0-9-])
//   /paiement/:token            → PaiementPage (UUID strict 36, sinon "Lien invalide")
//   /sejour/:token              → JourneyPage (idem double-pattern)
//   /sejour-ota/:token          → JourneyOtaPage (espace voyageur OTA, Lot O1)
//   /s/:code                    → ShortJourneyRedirectPage (lien court SMS, 12 hex)
//   /unsubscribe?token=         → UnsubscribePage (opt-out marketing 1 clic, Lot O3)
//   /services-payment/done      → ServicesPaymentReturnPage
const path = typeof window !== 'undefined' ? window.location.pathname : '/'
const publicRoute = matchPublicRoute(path)

const fallbackWrap = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f0f4f9',
  fontFamily: "'Raleway', sans-serif",
  padding: 24,
}
const fallbackCard = {
  background: '#ffffff',
  border: '1px solid #e4eaf3',
  borderRadius: 16,
  padding: 32,
  maxWidth: 420,
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 4px 16px rgba(33, 83, 135, 0.08)',
}
const fallbackTitle = {
  color: '#215387',
  fontFamily: "'Playfair Display', serif",
  fontSize: 22,
  margin: '0 0 12px',
}
const fallbackMsg = { color: '#444', fontSize: 14, lineHeight: 1.5, margin: '0 0 24px' }
const fallbackBtn = {
  background: '#0284c7',
  color: '#ffffff',
  border: 'none',
  borderRadius: 10,
  padding: '12px 24px',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: "'Raleway', sans-serif",
  fontSize: 14,
}

const Fallback = () => (
  <div style={fallbackWrap}>
    <div style={fallbackCard}>
      <h1 style={fallbackTitle}>Oups, une erreur est survenue.</h1>
      <p style={fallbackMsg}>
        Veuillez recharger la page. Si le problème persiste, contactez Dimitri.
      </p>
      <button style={fallbackBtn} onClick={() => window.location.reload()}>
        Recharger la page
      </button>
    </div>
  </div>
)

// PR #189 C24 — Suspense fallback pour React.lazy PaiementPage (F-M3 plan :
// crash runtime sans Suspense quand React.lazy charge un chunk async).
// Reuses style fallbackCard pour cohérence visuelle.
const Chargement = () => (
  <div style={fallbackWrap}>
    <div style={fallbackCard}>
      <h1 style={fallbackTitle}>Chargement...</h1>
      <p style={fallbackMsg}>Initialisation du paiement sécurisé.</p>
    </div>
  </div>
)

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <React.StrictMode>
    {/* Aperçu Vercel uniquement : « données réelles, ne rien enregistrer » (29/09). */}
    <ApercuBandeau />
    <Sentry.ErrorBoundary fallback={<Fallback />}>
      {publicRoute?.kind === 'form' ? (
        <PublicFormPage token={publicRoute.token} />
      ) : publicRoute?.kind === 'livret_token' ? (
        // H-2 pessimist : Suspense fallback pour React.lazy PublicLivretPage
        <Suspense fallback={<Chargement />}>
          <PublicLivretPage token={publicRoute.token} />
        </Suspense>
      ) : publicRoute?.kind === 'livret_module' ? (
        // PR-A93-1c (ADR-144) : route detail module standalone
        <Suspense fallback={<Chargement />}>
          <PublicLivretModuleDetail
            token={publicRoute.token}
            moduleId={publicRoute.moduleId}
          />
        </Suspense>
      ) : publicRoute?.kind === 'livret_qr' ? (
        // H-2 pessimist : Suspense fallback pour React.lazy PublicLivretQRPage
        <Suspense fallback={<Chargement />}>
          <PublicLivretQRPage slug={publicRoute.slug} />
        </Suspense>
      ) : publicRoute?.kind === 'paiement' ? (
        <Suspense fallback={<Chargement />}>
          <PaiementPage token={publicRoute.token} />
        </Suspense>
      ) : publicRoute?.kind === 'services_payment_return' ? (
        <Suspense fallback={<Chargement />}>
          <ServicesPaymentReturnPage />
        </Suspense>
      ) : publicRoute?.kind === 'journey' ? (
        <Suspense fallback={<Chargement />}>
          <JourneyPage token={publicRoute.token} />
        </Suspense>
      ) : publicRoute?.kind === 'journey_ota' ? (
        // Chantier OTA Lot O1 — espace voyageur OTA (blocs adaptatifs par source).
        <Suspense fallback={<Chargement />}>
          <JourneyOtaPage token={publicRoute.token} />
        </Suspense>
      ) : publicRoute?.kind === 'short_journey' ? (
        // Option B M1 (PR #962) — lien court SMS : résolution + redirection.
        <Suspense fallback={<Chargement />}>
          <ShortJourneyRedirectPage code={publicRoute.code} />
        </Suspense>
      ) : publicRoute?.kind === 'guest_link' ? (
        // CHANTIER LIEN COURT — lien court unifié /l/:code : résolution + redirection.
        <Suspense fallback={<Chargement />}>
          <GuestLinkRedirectPage code={publicRoute.code} />
        </Suspense>
      ) : publicRoute?.kind === 'recu' ? (
        // WAGON D LOT D-A (ADR-194) — document figé du séjour.
        <Suspense fallback={<Chargement />}>
          <RecuPage token={publicRoute.token} />
        </Suspense>
      ) : publicRoute?.kind === 'unsubscribe' ? (
        // CHANTIER OTA Lot O3 — désinscription marketing 1 clic (?token= lu
        // par le composant).
        <Suspense fallback={<Chargement />}>
          <UnsubscribePage />
        </Suspense>
      ) : (
        <App />
      )}
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)
